import { StatusCodes } from "http-status-codes";
import { CASES_APIS, DOCUMENTS, PATIENT_APIS } from "../utils/constants";
import {
  ICaseFilters,
  ICaseByIdFetchApiResponse,
  IPatientCaseTypesState,
  PatientCaseTypes,
  IAddCaseFetchApiResponse,
  IAddCaseState,
  ILastVisitResponse,
  IVisit,
  ICaseVisitFetchApiResponse,
  IEditCaseVisits,
  IAddCaseVisits,
  ICase,
  IAllCaseFetchResponse,
  IAllCasesFetchApiResponse,
  IAddVisitPayload,
} from "../interfaces/ICase";
import { IPostReferralResponse, IReferralForm } from "../interfaces/IReferral";
import Axios from "../utils/axiosInterceptor";
import { errorMessage } from "../utils/notificationUtils";
import { errorMessageModal } from "../utils/modalMessage";

export const fetchCases = async (
  filters: ICaseFilters,
  accessToken: string,
  hospitalId: string | number | undefined
): Promise<IAllCaseFetchResponse> => {
  try {
    const response = await Axios.post<IAllCaseFetchResponse>(
      `${CASES_APIS.ALL_CASES}/${hospitalId}/casesNew`,
      filters
    );

    if (response.status === StatusCodes.OK) {
      return {
        casesResponseNewList: response.data.casesResponseNewList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all cases",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all cases:", error);
    return {
      errorMessage: "Cannot fetch all cases",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const fetchCaseById = async (
  id: number
): Promise<ICaseByIdFetchApiResponse> => {
  try {
    const response = await Axios.get<ICaseByIdFetchApiResponse>(
      `${CASES_APIS.CASE_DETAILS}/${id}`
    );

    return {
      caseId: id,
      caseResponse: response.data.caseResponse,
      referralInfoResponseList: response.data.referralInfoResponseList,
      status: response.status,
    };
  } catch (error) {
    console.error("Error fetching case details:", error);
    return {
      caseId: id,
      errorMessage: "Cannot fetch case details",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const fetchCaseTypes = async (
  accessToken: string,
  schemeId: number
): Promise<PatientCaseTypes> => {
  try {
    const response = await Axios.get<Array<IPatientCaseTypesState>>(
      `${CASES_APIS.PATIENT_CASE_TYPES}/${schemeId}`
    );

    const parsedCases =
      typeof response.data === "string"
        ? JSON.parse(response.data)
        : response.data;
    return { portalNames: parsedCases, status: response.status };
  } catch (error) {
    console.error("Error fetching patient details:", error);
    throw new Error("Cannot fetch patient details");
  }
};

export const addPatientsCase = async (
  healthCareId: number,
  id: number,
  patientSchemeId: string,
  caseId: string
): Promise<IAddCaseFetchApiResponse> => {
  try {
    const url = `${PATIENT_APIS.ADD_PATIENT_CASE}/${healthCareId}/patients/${id}/patientSchemes/${patientSchemeId}/caseTypes/${caseId}/casesNew`;

    // Perform the POST request using Axios
    const response = await Axios.post<IAddCaseState>(
      url,
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return {
      fetchDetails: response.data,
      status: response.status,
      errorMessage: "",
    };
  } catch (error) {
    console.error("Error adding patient case:", error);

    const errorMessage = "Cannot add patient case";

    return {
      fetchDetails: null,
      errorMessage,
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const updatePatienceCase = async (
  id: number,
  patientSchemeId: string,
  caseId: string
): Promise<IAddCaseFetchApiResponse> => {
  try {
    const health_care_id = 1;
    const url = `${PATIENT_APIS.ADD_PATIENT_CASE}/${health_care_id}/patients/${id}/patientSchemes/${patientSchemeId}/caseTypes/1/casesNew/${caseId}`;

    const response = await Axios.patch<IAddCaseState>(url, {});

    return {
      fetchDetails: response.data,
      status: response.status,
      errorMessage: "",
    };
  } catch (error) {
    console.error("Error updating patient case:", error);
    return {
      fetchDetails: null,
      errorMessage: "Cannot update patient case",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const uploadDocs = async (formData: FormData, accessToken?: string) => {
  try {
    const response = await Axios.post(DOCUMENTS.UPLOAD_DOCUMENTS, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading documents:", error);
    return {
      errorMessage: "Cannot upload document",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const readOcrDoc = async (formData: any, accessToken: string) => {
  try {
    const response = await Axios.post(DOCUMENTS.READ_DOCS_OCR, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Error reading OCR document:", error);
    errorMessage(error?.response?.data);
    return {
      errorMessage: error?.response?.data || "Cannot read file",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const postReferralData = async (
  caseRegId: string,
  formData: IReferralForm,
  accessToken: string
): Promise<IPostReferralResponse> => {
  try {
    const response = await Axios.post(
      `${CASES_APIS.CASE_REFERRAL}/${caseRegId}/referralInfo`,
      formData
    );

    return { updateReferralResponse: response.data, status: response.status };
  } catch (error) {
    console.error("Error posting referral data:", error);
    return {
      updateReferralResponse: null,
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const putReferralData = async (
  caseId: number | undefined,
  formData: IReferralForm,
  caseRegId: number
): Promise<IPostReferralResponse> => {
  try {
    const response = await Axios.patch(
      `${CASES_APIS.CASE_REFERRAL}/${caseId}/referralInfo/${caseRegId}`,
      formData
    );

    return { updateReferralResponse: response.data, status: response.status };
  } catch (error) {
    console.error("Error updating referral data:", error);
    return {
      errorMessage: "Cannot update referral",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const addVisit = async (
  id: number,
  formValues: IAddVisitPayload
): Promise<IAddCaseVisits> => {
  try {
    const url = `${CASES_APIS.CASES_VISIT}/${id}/visit_info`;
    const response = await Axios.post<IVisit>(url, formValues);

    return { addCaseResp: response.data, status: response.status };
  } catch (error) {
    console.error("Error adding patient visit:", error);
    return {
      addCaseResp: null,
      errorMessage: "Cannot add patient visit",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const editVisit = async (
  id: number,
  visitId: number,
  formValues: IAddVisitPayload
): Promise<IEditCaseVisits> => {
  try {
    const url = `${CASES_APIS.CASES_VISIT}/${id}/visit_info/${visitId}`;
    const response = await Axios.patch<IVisit>(url, formValues);

    return { editResp: response.data, status: response.status };
  } catch (error) {
    console.error("Error editing patient visit:", error);
    return {
      editResp: null,
      errorMessage: "Cannot edit patient visit",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const fetchCasesVisits = async (
  id: number
): Promise<ICaseVisitFetchApiResponse> => {
  try {
    const response = await Axios.get<Array<IVisit>>(
      `${CASES_APIS.CASES_VISIT}/${id}/visit_info/all`
    );

    return { caseId: id, visits: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching case visits:", error);
    return {
      caseId: id,
      visits: [],
      errorMessage: "Cannot fetch all cases",
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};

export const fetchCaseVisit = async (
  id: number
): Promise<ILastVisitResponse> => {
  try {
    const response = await Axios.get<Array<IVisit>>(
      `${CASES_APIS.CASES_VISIT}/${id}/visit_info`
    );

    return { getLastVisit: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching case visit:", error);
    return {
      getLastVisit: null,
      status: StatusCodes.INTERNAL_SERVER_ERROR,
    };
  }
};
